import { executeFetch, type Fetcher } from "../FetchSupport.ts";
import { assertSourceUrl } from "../SourceRegistry.ts";
import { NetworkError, type HttpRequest, type ImageRequest, type PreparedImage } from "../types.ts";

export interface PersonalProxyConfig {
  url: string;
  format: "json" | "url-template";
}

export function validateProxyConfig(config: PersonalProxyConfig): void {
  let url: URL;
  try {
    url = new URL(config.url.replace("{url}", encodeURIComponent("https://example.com/")));
  } catch {
    throw new NetworkError("INVALID_RESPONSE", "URL de proxy inválida.");
  }
  if (
    url.username ||
    url.password ||
    url.hash ||
    (url.protocol !== "https:" &&
      !(url.protocol === "http:" && ["localhost", "127.0.0.1"].includes(url.hostname))) ||
    !["json", "url-template"].includes(config.format) ||
    (config.format === "url-template" && !config.url.includes("{url}"))
  )
    throw new NetworkError(
      "INVALID_RESPONSE",
      "El proxy debe usar HTTPS o localhost y un formato válido.",
    );
}

export class PersonalProxyTransport {
  constructor(
    private readonly config: () => PersonalProxyConfig | undefined,
    private readonly fetcher: Fetcher = (input, init) => fetch(input, init),
  ) {}

  isAvailable(): boolean {
    return !!this.config();
  }
  text(request: HttpRequest & { responseType: "text" }) {
    return this.execute(request, (bytes) => new TextDecoder().decode(bytes));
  }
  bytes(request: HttpRequest & { responseType: "bytes" }) {
    return this.execute(request, (bytes) => bytes);
  }

  async prepareImage(request: ImageRequest): Promise<PreparedImage> {
    const response = await this.bytes({
      ...request,
      method: "GET",
      headers: { ...request.headers, ...(request.referer ? { Referer: request.referer } : {}) },
      responseType: "bytes",
      purpose: "image",
      credentials: "omit",
      cache: { ttlMs: 0, scope: "session" },
    });
    if (response.status >= 400)
      throw new NetworkError(
        "HTTP_ERROR",
        `Imagen: HTTP ${response.status}`,
        response.status,
        "personal-proxy",
      );
    const url = URL.createObjectURL(
      new Blob([response.body.slice().buffer as ArrayBuffer], {
        type: response.headers["content-type"] ?? "application/octet-stream",
      }),
    );
    return { url, delivery: "blob", release: () => URL.revokeObjectURL(url) };
  }

  private execute<T extends string | Uint8Array>(
    request: HttpRequest,
    decode: (bytes: Uint8Array) => T,
  ) {
    assertSourceUrl(request.sourceId, request.url);
    const config = this.config();
    if (!config) throw new NetworkError("BRIDGE_UNAVAILABLE", "Configurá tu proxy personal.");
    validateProxyConfig(config);
    if (
      config.format === "url-template" &&
      (request.method !== "GET" || Object.keys(request.headers ?? {}).length)
    )
      throw new NetworkError(
        "INVALID_RESPONSE",
        "Este proxy por URL admite GET sin cabeceras. Usá el formato JSON para consultas con Referer o POST.",
      );
    const endpoint =
      config.format === "url-template"
        ? config.url.replaceAll("{url}", encodeURIComponent(request.url))
        : config.url;
    const init: RequestInit =
      config.format === "json"
        ? {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              url: request.url,
              method: request.method,
              headers: request.headers ?? {},
              body: request.body,
            }),
          }
        : { method: "GET" };
    return executeFetch(
      request,
      "personal-proxy",
      this.fetcher,
      endpoint,
      { ...init, credentials: "omit", mode: "cors" },
      decode,
      undefined,
      () => request.url,
    );
  }
}
